<template>
  <footer class="app-footer">
    <div class="container">
      <div class="footer-grid">
        <!-- Colonne 1 : Logo et description -->
        <div class="footer-col">
          <div class="footer-logo">
            <router-link to="/" class="logo">OKOLISY</router-link>
          </div>
          <p class="footer-description">
            La première plateforme gabonaise de vente et d'achat d'articles de mode d'occasion.
            Rejoignez la communauté et donnez une seconde vie à vos vêtements !
          </p>
          <div class="social-links">
            <a href="https://www.tiktok.com/@okolisy?_r=1&_t=ZS-98snp2dycBr" class="social-link" aria-label="TikTok">
              <font-awesome-icon :icon="['fab', 'tiktok']" />
            </a>
          </div>
        </div>

        <!-- Colonne 2 : Liens rapides -->
        <div class="footer-col">
          <h3>Liens rapides</h3>
          <ul class="footer-links">
            <li><router-link to="/">Accueil</router-link></li>
            <li><router-link to="/a-propos">À propos</router-link></li>
            <!-- ✅ Modification ici : lien avec vérification d'auth -->
            <li>
              <a href="#" @click.prevent="handleSellClick">Vendre un article</a>
            </li>
            <li><router-link to="/comment-ca-marche">Comment ça marche ?</router-link></li>
            <li><a href="#">Foire aux questions</a></li>
            <li><a href="#">Blog</a></li>
          </ul>
        </div>

        <!-- Colonne 3 : Mon compte -->
        <div class="footer-col">
          <h3>Mon compte</h3>
          <ul class="footer-links">
            <li v-if="!isAuthenticated">
              <a href="#" @click.prevent="showLoginModal">Se connecter</a>
            </li>
            <li v-if="!isAuthenticated">
              <a href="#" @click.prevent="showRegisterModal">S'inscrire</a>
            </li>
            <li v-if="isAuthenticated">
              <router-link to="/mon-profil">Mon profil</router-link>
            </li>
            <li v-if="isAuthenticated">
              <router-link to="/mes-commandes">Mes commandes</router-link>
            </li>
            <li><router-link to="/conditions-generales">Conditions générales</router-link></li>
            <li><router-link to="/politique-confidentialite">Politique de confidentialité</router-link></li>
          </ul>
        </div>

        <!-- Colonne 4 : Contact -->
        <div class="footer-col">
          <h3>Contact</h3>
          <ul class="footer-contact">
            <li>
              <font-awesome-icon icon="envelope" />
              <a href="mailto:okolisyadmin@gmail.com">okolisyadmin@gmail.com</a>
            </li>
            <li>
              <font-awesome-icon icon="phone" />
              <a href="tel:+241076396575">+241 076 39 65 75</a>
            </li>
            <li>
              <font-awesome-icon icon="map-marker-alt" />
              <span>Libreville, Gabon</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Newsletter -->
      <div class="newsletter-section">
        <h3>Restez informé</h3>
        <p>Inscrivez-vous à notre newsletter pour recevoir nos offres exclusives</p>
        <form class="newsletter-form" @submit.prevent="subscribeNewsletter">
          <input 
            type="email" 
            v-model="newsletterEmail" 
            placeholder="Votre adresse email"
            required
          >
          <button type="submit" :disabled="newsletterLoading">
            {{ newsletterLoading ? '...' : 'S\'abonner' }}
          </button>
        </form>
        <p v-if="newsletterMessage" class="newsletter-message" :class="newsletterMessageType">
          {{ newsletterMessage }}
        </p>
      </div>

      <!-- Copyright -->
      <div class="footer-bottom">
        <p>&copy; {{ currentYear }} OKOLISY. Tous droits réservés.</p>
        <p class="credit">Développé par <a href="#" target="_blank">Lisy NGUIZI</a></p>
      </div>
    </div>

    <!-- Modals d'authentification -->
    <AuthModals 
      :show-login-modal="showLogin" 
      :show-register-modal="showRegister"
      @close-modals="closeModals"
      @switch-to-login="switchToLogin"
      @switch-to-register="switchToRegister"
      @login-success="handleAuthSuccess"
      @register-success="handleAuthSuccess"
    />
  </footer>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useClientAuthStore } from '@/Auth/stores/clientAuthStore';
import AuthModals from '@/Auth/AuthModals.vue';

export default defineComponent({
  name: 'AppFooter',
  components: {
    AuthModals
  },
  setup() {
    const router = useRouter();
    const clientAuth = useClientAuthStore();
    const newsletterEmail = ref('');
    const newsletterLoading = ref(false);
    const newsletterMessage = ref('');
    const newsletterMessageType = ref('');
    const showLogin = ref(false);
    const showRegister = ref(false);

    const isAuthenticated = computed(() => clientAuth.state.isAuthenticated);
    const currentYear = computed(() => new Date().getFullYear());

    // ✅ Nouvelle fonction handleSellClick identique à celle du header
    const handleSellClick = (event: Event) => {
      event.preventDefault();
      if (!isAuthenticated.value) {
        showLoginModal();
      } else {
        router.push('/vendre');
      }
    };

    const showLoginModal = () => {
      showLogin.value = true;
      showRegister.value = false;
    };

    const showRegisterModal = () => {
      showRegister.value = true;
      showLogin.value = false;
    };

    const closeModals = () => {
      showLogin.value = false;
      showRegister.value = false;
    };

    const switchToLogin = () => {
      showRegister.value = false;
      showLogin.value = true;
    };

    const switchToRegister = () => {
      showLogin.value = false;
      showRegister.value = true;
    };

    const handleAuthSuccess = () => {
      closeModals();
    };

    const subscribeNewsletter = async () => {
      if (!newsletterEmail.value) return;
      
      newsletterLoading.value = true;
      newsletterMessage.value = '';
      
      try {
        // Appel API pour newsletter (à créer si besoin)
        // await $api.post('/api/newsletter', { email: newsletterEmail.value });
        
        newsletterMessage.value = 'Merci pour votre inscription !';
        newsletterMessageType.value = 'success';
        newsletterEmail.value = '';
        
        setTimeout(() => {
          newsletterMessage.value = '';
        }, 3000);
      } catch (error) {
        newsletterMessage.value = 'Une erreur est survenue. Veuillez réessayer.';
        newsletterMessageType.value = 'error';
      } finally {
        newsletterLoading.value = false;
      }
    };

    return {
      isAuthenticated,
      currentYear,
      newsletterEmail,
      newsletterLoading,
      newsletterMessage,
      newsletterMessageType,
      showLogin,
      showRegister,
      handleSellClick,      // ✅ Ajouté
      showLoginModal,
      showRegisterModal,
      closeModals,
      switchToLogin,
      switchToRegister,
      handleAuthSuccess,
      subscribeNewsletter
    };
  }
});
</script>

<style lang="scss" scoped>
@use '@/styles/variables' as *;

.app-footer {
  background: $gris-fonce;
  color: $gris-clair;
  padding: 50px 0 20px;
  margin-top: auto;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
}

.footer-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  margin-bottom: 40px;
  padding-bottom: 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
}

.footer-col {
  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 20px;
    color: $orange-doux;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 40px;
      height: 2px;
      background: $orange-doux;
    }
  }
}

.footer-logo {
  margin-bottom: 15px;
  
  .logo {
    font-size: 1.8rem;
    font-weight: bold;
    color: $orange-doux;
    text-decoration: none;
    
    &:hover {
      color: lighten($orange-doux, 10%);
    }
  }
}

.footer-description {
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 20px;
  color: $gris-clair;
}

.social-links {
  display: flex;
  gap: 15px;
  
  .social-link {
    width: 36px;
    height: 36px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $blanc;
    transition: all 0.3s ease;
    
    &:hover {
      background: $orange-doux;
      transform: translateY(-3px);
    }
  }
}

.footer-links {
  list-style: none;
  padding: 0;
  
  li {
    margin-bottom: 10px;
    
    a {
      color: $gris-clair;
      text-decoration: none;
      transition: color 0.3s ease;
      font-size: 0.9rem;
      cursor: pointer;
      
      &:hover {
        color: $orange-doux;
      }
    }
  }
}

.footer-contact {
  list-style: none;
  padding: 0;
  
  li {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
    font-size: 0.9rem;
    
    svg {
      color: $orange-doux;
      width: 18px;
    }
    
    a, span {
      color: $gris-clair;
      text-decoration: none;
      
      &:hover {
        color: $orange-doux;
      }
    }
  }
}

.newsletter-section {
  text-align: center;
  margin-bottom: 40px;
  padding-bottom: 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  h3 {
    font-size: 1.2rem;
    margin-bottom: 10px;
    color: $orange-doux;
  }
  
  p {
    font-size: 0.9rem;
    margin-bottom: 20px;
    color: $gris-clair;
  }
  
  .newsletter-form {
    display: flex;
    justify-content: center;
    gap: 10px;
    max-width: 500px;
    margin: 0 auto;
    
    input {
      flex: 1;
      padding: 12px 15px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      background: rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      color: $blanc;
      font-size: 0.9rem;
      
      &::placeholder {
        color: $gris-moyen;
      }
      
      &:focus {
        outline: none;
        border-color: $orange-doux;
      }
    }
    
    button {
      padding: 12px 25px;
      background: $orange-fonce;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: background 0.3s ease;
      
      &:hover:not(:disabled) {
        background: darken($orange-fonce, 10%);
      }
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
  
  .newsletter-message {
    margin-top: 15px;
    font-size: 0.85rem;
    
    &.success {
      color: #28a745;
    }
    
    &.error {
      color: #dc3545;
    }
  }
}

.footer-bottom {
  text-align: center;
  padding-top: 20px;
  font-size: 0.8rem;
  
  p {
    margin-bottom: 5px;
    color: $gris-moyen;
  }
  
  .credit a {
    color: $orange-doux;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

// Responsive
@media (max-width: 600px) {
  .app-footer {
    padding: 40px 0 20px;
  }
  
  .newsletter-form {
    flex-direction: column;
    
    button {
      width: 100%;
    }
  }
}
</style>